import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { VStack } from "../../../../styled-system/jsx";
import { useDSPGeoDatasetsQuery } from "../hooks/useDSPGeoDatasetsQuery";
import getMetricSpec from "../../merchandising/utils/metrics";
import LocationChart from "./LocationChart";
import Loading from "../../../components/core/blocks/Loading";
import { DSPGeoTable } from "./DSPGeoTable";
import { mapRegion } from "../utils/mapRegion";

type Props = {
  showAll?: boolean;
  lineItemId?: string | number | null;
  orderId?: string | number | null;
};

const DSPGeoDashboard = ({ showAll = false, lineItemId = null, orderId = null }: Props) => {
  const { t } = useTranslation(["dsp"]);
  const [selectedMetric, setSelectedMetric] = useState("dsp_total_detail_page_views");

  const { data: geoResponse, isLoading: isDSPGeoDataLoading } = useDSPGeoDatasetsQuery({
    lineItemId,
    orderId,
    showAll,
  });
  const dspGeoData = useMemo(() => geoResponse?.data ?? [], [geoResponse]);

  const metric = useMemo(() => getMetricSpec(selectedMetric), [selectedMetric]);

  const geoData = useMemo(() => {
    return dspGeoData
      .map((d) => {
        const state = mapRegion(d.country, d.region);
        if (!state) {
          return null;
        }
        return {
          "hc-key": state,
          value: metric.accessor(d) || 0,
        };
      })
      .filter((d) => d !== null);
  }, [dspGeoData, metric]);

  const metricOptions = [
    { value: "dsp_total_detail_page_views", label: t(($) => $.dsp["Detail Page Views"]) },
    { value: "dsp_total_add_to_cart", label: t(($) => $.dsp["Add to Carts"]) },
    { value: "dsp_total_new_to_brand_dpvs", label: t(($) => $.dsp["New to Brand DPV"]) },
    { value: "dsp_total_units", label: t(($) => $.dsp["Total Units"]) },
  ];

  const regionData = useMemo(() => {
    return dspGeoData.map((d) => {
      const region = (d.region || t(($) => $.dsp["Other"])) + ", " + d.country;
      return { ...d, region };
    });
  }, [dspGeoData, t]);

  if (isDSPGeoDataLoading) {
    return <Loading height={"300px"} fullPage={true} />;
  }

  return (
    <VStack gap={4} width={"100%"} alignItems={"stretch"}>
      <LocationChart
        regionData={regionData}
        geoData={geoData}
        metricOptions={metricOptions}
        selectedMetric={selectedMetric}
        onSelectMetric={setSelectedMetric}
      />
      <DSPGeoTable lineItemId={lineItemId} orderId={orderId} showAll={showAll} />
    </VStack>
  );
};

export default DSPGeoDashboard;
